import {
  useAppointment,
  useAppointmentCreate,
  useAppointmentEdit,
  useMutationRecurringAppointmentCreate
} from "@gymflow/api";
import {
  AppointmentPostDTO,
  AppointmentStatus,
  RecurringAppointmentPostDTO,
} from "@gymflow/types";
import { isAxiosError } from "axios";
import { NO_AVAILABLE_SLOTS_ERROR_CODES } from "libs/helpers/src/constants/ManuallyHandledErrorCodes";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

import { ModalContext, useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import { NotAvailableModal } from "../NotAvailableModal/NotAvailableModal";
import { SideBarAppointmentForm } from "./SideBarAppointmentForm";

interface SideBarAppointmentFormProviderProps {
  children: ReactNode;
}

interface SideBarAppointmentFormProviderContextType {
  openEdit: (params: {
    appointmentId: number;
    onClose?: () => Promise<void>;
  }) => void;
  openNew: (params: {
    referenceDate: string;
    onClose?: () => Promise<void>;
  }) => void;
}

export const SideBarAppointmentFormProviderContext =
  createContext<SideBarAppointmentFormProviderContextType>({} as any);

export function SideBarAppointmentFormProvider({
  children,
}: SideBarAppointmentFormProviderProps) {
  const { setModal, hide } = useContext(ModalContext);

  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [sideBarParams, setSideBarParams] = useState<{
    appointmentId?: number;
    onClose?: () => void;
    initialDateAndTime?: string;
  }>();

  const { api } = useGymflowModels();
  const { club } = useClubSettings();
  const { data: editing, isFetching } = useAppointment({
    api,
    appointmentId: sideBarParams?.appointmentId,
  });

  const errorHandler = useCallback(
    (
      e: any,
      payload:
        | {
            isRecurring: false;
            values: Omit<AppointmentPostDTO, "status">;
          }
        | { isRecurring: true; values: RecurringAppointmentPostDTO },
    ) => {
      setIsVisible(false);
      if (payload.isRecurring) {
        return;
      }
      if (
        isAxiosError(e) &&
        NO_AVAILABLE_SLOTS_ERROR_CODES.includes(e.response?.data.error_code)
      ) {
        setModal(
          <NotAvailableModal
            errorCode={e.response?.data.error_code}
            onConfirm={async () => {
              await createAppointmentMutation.mutateAsync({
                ...payload.values,
                availabilityValidationIgnored: true,
                status: "BOOKED",
              });
              hide();
              setIsLoaded(false);
            }}
            onCancel={() => {
              hide();
              setIsVisible(true);
            }}
          />,
        );
      } else {
        setIsLoaded(false);
      }
    },
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const createAppointmentMutation = useAppointmentCreate({
    api,
    tz: club?.timezone,
  });
  const editAppointmentMutation = useAppointmentEdit({
    api,
    tz: club?.timezone,
  });
  const createRecurringAppointmentMutation =
    useMutationRecurringAppointmentCreate({ api });

  return (
    <SideBarAppointmentFormProviderContext.Provider
      value={{
        openEdit: ({ appointmentId, onClose }) => {
          setSideBarParams({ appointmentId, onClose });
          setIsLoaded(true);
          setIsVisible(true);
        },
        openNew: ({ referenceDate, onClose }) => {
          setSideBarParams({
            initialDateAndTime: referenceDate,
            onClose,
          });
          setIsVisible(true);
          setIsLoaded(true);
        },
      }}
    >
      {isLoaded && (
        <div>
          <SideBarAppointmentForm
            defaultStartDate={sideBarParams?.initialDateAndTime}
            isVisible={isVisible}
            isLoaded={isLoaded}
            onClose={() => {
              setIsVisible(false);
              setIsLoaded(false);
              if (sideBarParams?.onClose) {
                sideBarParams.onClose();
              }
              setSideBarParams(undefined);
            }}
            value={editing}
            isLoading={isFetching}
            onChange={async (payload) => {
              try {
                if (sideBarParams?.appointmentId) {
                  await editAppointmentMutation.mutateAsync({
                    appointmentId: sideBarParams.appointmentId,
                    patchedFields:
                      payload.values as Partial<AppointmentPostDTO>,
                  });
                } else {
                  if (payload.isRecurring) {
                    await createRecurringAppointmentMutation.mutateAsync(
                      payload.values,
                    );
                  } else {
                    const newAppointment = {
                      ...payload.values,
                      status: "BOOKED" as AppointmentStatus,
                    };
                    await createAppointmentMutation.mutateAsync(newAppointment);
                  }
                }
                setIsLoaded(false);
                setIsVisible(false);
              } catch (e) {
                errorHandler(e, payload);
              }
              if (sideBarParams?.onClose) {
                sideBarParams.onClose();
              }
            }}
          />
        </div>
      )}
      {children}
    </SideBarAppointmentFormProviderContext.Provider>
  );
}
